import { datadogLogs } from "@datadog/browser-logs";
import firebase from "firebase/app";
import type { ObservabilityContext } from "ts/other";

const CLIENT_TOKEN = "puba74059d93708094bd1fa71cefcc29ada";
const SITE = import.meta.env.VITE_DD_SITE ?? "datadoghq.eu";
const SERVICE = import.meta.env.VITE_DD_SERVICE ?? "ffair-console";
const ENV = import.meta.env.VITE_DD_ENV ?? "prod";

const isActivateDataDog = (): boolean => {
  if (import.meta.env.MODE !== "production") return false;
  if (import.meta.env.VITE_PROJECT_ID !== "ffair-live") return false;
  // No credentials → no init. Avoids partial / broken telemetry in builds
  // that simply forgot to wire the env vars (e.g. preview deploys).
  if (!CLIENT_TOKEN) {
    console.warn("Datadog disabled: VITE_DD_CLIENT_TOKEN missing");
    return false;
  }
  return true;
};

// Strip auth tokens / API keys / emails from arbitrary URL strings so they
// don't end up in Datadog events. Keeps the path so dashboards still group by
// route, replaces only the value portion of sensitive query params.
const SENSITIVE_QUERY_PARAMS = /([?&](?:token|access[-_]token|id[-_]token|key|auth|password|email)=)[^&#]+/gi;

const scrubUrl = (url: string | undefined): string | undefined => {
  if (!url) return url;
  return url.replace(SENSITIVE_QUERY_PARAMS, "$1***");
};

// =============================================================================
// Noise filters — single source of truth for events we don't want in DD.
//
// Each entry declares which field to test (`url` or `message`) and an optional
// `statusCode` gate so we can drop browser-aborted traffic (status=0) while
// keeping real 4xx/5xx from the same endpoint visible. Applied in Logs
// `beforeSend`.
//
// Adding a message filter is appropriate ONLY when:
//   1. Produced by a library / browser / extension, not our own code.
//   2. Self-recovers or is a no-op (we / the user can't act on it).
//   3. Verified not to mask any real failure mode.
//
// Reference (Sentry's de-facto curated list):
//   https://docs.sentry.io/platforms/javascript/configuration/filtering/
// =============================================================================

type NoiseFilter = {
  /** Which field of the event to test against. */
  target: "url" | "message";
  pattern: RegExp;
  /**
   * Drop only when the resource status code equals this. Used for background
   * aborts (status=0 = "browser cancelled the request") so real failures from
   * the same URL — expired auth, quota, outage — still surface.
   */
  statusCode?: number;
};

const NOISE_FILTERS: NoiseFilter[] = [
  // -- Health-check probes (network-status.store.ts) -------------------------
  // HEAD pings whose 404 / opaque responses are by design. Drop unconditionally.
  { target: "url", pattern: /^https:\/\/firestore\.googleapis\.com\/?$/ },
  { target: "url", pattern: /^https:\/\/firebasestorage\.googleapis\.com\/?$/ },
  { target: "url", pattern: /^https:\/\/identitytoolkit\.googleapis\.com\/?$/ },
  {
    target: "url",
    pattern: /^https:\/\/[^/]*cloudfunctions\.net\/__health_probe__/,
  },
  { target: "url", pattern: /^https:\/\/js\.stripe\.com\/v3\/?$/ },
  { target: "url", pattern: /^https:\/\/proxy\.ffair\.show\/health/ },
  // Application-side health endpoints (api, notification, storage services).
  { target: "url", pattern: /\/health(\?|$)/ },
  // Internet probe — `${origin}/robots.txt?_=…`.
  { target: "url", pattern: /\/robots\.txt\?_=/ },

  // -- Firebase background channels (status=0 only) --------------------------
  // Long-poll & token-refresh aborts when the browser unloads / navigates /
  // recycles the connection. SDK reconnects automatically. Real responses
  // (4xx/5xx) from these URLs still pass because of the statusCode gate.
  {
    target: "url",
    pattern: /\/google\.firestore\.v1\.Firestore\/(Listen|Write)\/channel/,
    statusCode: 0,
  },
  {
    target: "url",
    pattern: /^https:\/\/securetoken\.googleapis\.com\/v1\/token/,
    statusCode: 0,
  },
  // Same two endpoints surface via console.error("XHR error <METHOD> <URL>")
  // emitted by Firebase WebChannel itself — captured by forwardConsoleLogs
  // without an associated http event, so they need a message-side filter too.
  {
    target: "message",
    pattern: /^(?:XHR|Fetch) error \w+ https?:\/\/[^\s]*\/google\.firestore\.v1\.Firestore\/(?:Listen|Write)\/channel/i,
  },
  {
    target: "message",
    pattern: /^(?:XHR|Fetch) error \w+ https?:\/\/securetoken\.googleapis\.com\/v1\/token/i,
  },
  // Firestore security-rule reject. Fires when a stale listener races a
  // logout/token-rotation/route-change and pings a doc the now-current
  // identity can't read. Self-recovers — the offending listener tears down
  // on the next render. Real misconfigured rules show up during QA, not
  // as a steady drumbeat in prod.
  { target: "message", pattern: /^Missing or insufficient permissions\.?$/i },
  // Verbatim wording from the Firebase Functions / Firestore SDK for any
  // transport-level failure (TCP reset, DNS blip, request abort, captive
  // portal). No actionable info — the SDK retries the next call. Matches
  // both the bare SDK message and the wrapped variants from our own
  // try/catch logs (e.g. useLastTimelineAction's "Error occurred …").
  {
    target: "message",
    pattern: /A network error \(such as timeout, interrupted connection or unreachable host\) has occurred/i,
  },

  // -- Browser quirks --------------------------------------------------------
  // ResizeObserver fires a synthetic error when its callback triggers another
  // resize. Specced behaviour, harmless.
  { target: "message", pattern: /ResizeObserver loop limit exceeded/i },
  {
    target: "message",
    pattern: /ResizeObserver loop completed with undelivered notifications/i,
  },
  // Cross-origin script error with no diagnostic body.
  { target: "message", pattern: /^Script error\.?$/i },

  // -- Browser-translate extensions (Chrome / Edge) --------------------------
  // Translate rewrites text nodes; DOM observers held by NodeIterator /
  // NodeFilter / URLSearchParams are torn off mid-iteration. React recovers
  // via re-render. Covers bare messages and React Router's "caught the
  // following error during render" wrapper.
  { target: "message", pattern: /Failed to execute 'removeChild' on 'Node'/i },
  { target: "message", pattern: /Failed to execute 'insertBefore' on 'Node'/i },
  {
    target: "message",
    pattern: /The node to be removed is not a child of this node/i,
  },
  {
    target: "message",
    pattern: /The provided callback is no longer runnable/i,
  },

  // -- Password-manager extensions ------------------------------------------
  // LastPass / 1Password postMessage bus dropping in-flight requests on
  // navigation. Not our code, no user impact.
  {
    target: "message",
    pattern: /Object Not Found Matching Id:\d+, MethodName:/i,
  },

  // -- Aborted network requests ---------------------------------------------
  // Navigation / AbortController / fetch retry teardown. Not server failures.
  { target: "message", pattern: /^AbortError:/i },
  { target: "message", pattern: /The operation was aborted/i },
  { target: "message", pattern: /The user aborted a request/i },
  { target: "message", pattern: /^cancell?ed$/i },
  { target: "message", pattern: /^cancelado$/i },
  // Datadog emits "XHR error <METHOD> <URL>" / "Fetch error <METHOD> <URL>"
  // log lines whenever a request finishes with status 0 — i.e. no response
  // ever arrived. That's a browser-side cancel: tab navigation, page unload,
  // AbortController, mobile signal drop. Real 4xx/5xx from the same routes
  // still have a non-zero status and pass through.
  {
    target: "message",
    pattern: /^(?:XHR|Fetch) error \w+ https?:\/\//i,
    statusCode: 0,
  },

  // -- Detached print iframe ------------------------------------------------
  // Pending observers inside the iframe surface once on `afterprint` teardown
  // (see printShowInfo.ts). One-shot, then stops.
  {
    target: "message",
    pattern: /Failed to execute 'nextNode' on 'NodeIterator'/i,
  },

  // -- socket.io / engine.io polling-transport blips ------------------------
  // Library auto-reconnects. Surfaces as `console.error("Connection Error:", err)`.
  { target: "message", pattern: /^Connection Error: /i },
  { target: "message", pattern: /xhr poll error/i },
  { target: "message", pattern: /xhr post error/i },
  { target: "message", pattern: /websocket error/i },

  // -- Code-split chunk load failure ----------------------------------------
  // Usually a tab outlived the deploy that invalidated the chunk. Surfaced to
  // the user via the in-app "reload" banner; the raw error is duplicate.
  { target: "message", pattern: /Loading (CSS )?chunk \d+ failed/i },
  { target: "message", pattern: /ChunkLoadError/i },

  // -- Third-party SDK lifecycle --------------------------------------------
  // Library threw a non-Error value from inside a Promise.
  { target: "message", pattern: /^Non-Error promise rejection captured/i },
];

type FilterableEvent = {
  url?: string;
  message?: string;
  statusCode?: number;
};

const shouldDrop = (event: FilterableEvent): boolean => {
  for (const filter of NOISE_FILTERS) {
    if (
      filter.statusCode !== undefined &&
      event.statusCode !== filter.statusCode
    )
      continue;
    const value = filter.target === "url" ? event.url : event.message;
    if (!value) continue;
    if (filter.pattern.test(value)) return true;
  }
  return false;
};

// =============================================================================
// Axios error enrichment for `dataDogError`.
//
// The default `AxiosError: Request failed with status code 401` message is
// useless in DD — no method, no URL, no body, no correlation id. Stripping
// these fields out by hand at every call site is repetitive and error-prone,
// so `dataDogError` does it automatically when it sees an axios-shaped error.
// =============================================================================

const MAX_RESPONSE_BODY_CHARS = 1024;

const truncateString = (s: string, max: number): string =>
  s.length > max ? `${s.slice(0, max)}…(+${s.length - max} more chars)` : s;

const safeStringify = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

type AxiosLikeError = {
  isAxiosError?: boolean;
  message?: string;
  code?: string;
  config?: {
    method?: string;
    url?: string;
    baseURL?: string;
    timeout?: number;
    __authUid?: string;
  };
  response?: {
    status?: number;
    statusText?: string;
    headers?: Record<string, string | undefined>;
    data?: unknown;
  };
};

const isAxiosLike = (error: unknown): error is AxiosLikeError => {
  if (typeof error !== "object" || error === null) return false;
  const e = error as Record<string, unknown>;
  return Boolean(e.isAxiosError) || ("config" in e && "response" in e);
};

const extractAxiosFields = (error: AxiosLikeError): Record<string, unknown> => {
  const { config, response } = error;
  const headers = response?.headers ?? {};
  const data = response?.data as Record<string, unknown> | undefined;

  const fullUrl =
    config?.baseURL && config.url
      ? `${config.baseURL.replace(/\/$/, "")}/${config.url.replace(/^\//, "")}`
      : config?.url;

  return {
    kind: "axios",
    method: config?.method?.toUpperCase(),
    url: scrubUrl(config?.url),
    fullUrl: scrubUrl(fullUrl),
    status: response?.status,
    statusText: response?.statusText,
    // Axios `code` covers transport-level failures (ECONNABORTED, ERR_NETWORK,
    // ERR_BAD_REQUEST) — useful when there is no response at all.
    code: error.code,
    timeoutMs: config?.timeout,
    // Server-provided correlation IDs, priority order. Lets us jump from a
    // failing client log straight to the matching server log entry.
    requestId:
      headers["x-request-id"] ??
      headers["x-correlation-id"] ??
      headers["x-trace-id"] ??
      headers["x-datadog-trace-id"],
    // Our api-v2 services return `{ code, message, ... }` on 4xx — surface
    // them as top-level attributes so DD dashboards can group by error code.
    backendCode: data?.code ?? data?.errorCode,
    backendMessage: data?.message,
    responseBody: truncateString(safeStringify(data), MAX_RESPONSE_BODY_CHARS),
    // Stamped on the request by `onRequest` so a stale-after-impersonation
    // 401 is identifiable in the dashboard.
    authUid: config?.__authUid,
  };
};

let initialized = false;

/**
 * Initialise Datadog Logs as early as possible (before React mounts).
 *
 * Why early: the previous implementation called `init()` only AFTER login,
 * so any error/page-load event on /login, /signup, or in pre-auth bootstrap
 * code was invisible. Init now fires on app boot; user identity is attached
 * later via `dataDogIdentify` once Firebase resolves auth state.
 *
 * No-op outside the production `ffair-live` build, and idempotent (safe to
 * call from multiple entry points).
 */
export const initDatadog = (): void => {
  if (initialized) return;
  if (!isActivateDataDog()) return;

  initialized = true;

  datadogLogs.init({
    clientToken: CLIENT_TOKEN as string,
    site: SITE,
    service: SERVICE,
    env: ENV,
    version: __APP_VERSION__,
    // Forward unhandled errors and explicit console.error calls only.
    // `console.warn` is intentionally excluded — third-party deprecation
    // noise (firebase 8, MUI 4, draft-js) would otherwise flood DD Logs and
    // inflate the ingestion bill without surfacing actionable signal.
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error", "warn"],
    sessionSampleRate: 100,
    beforeSend: (log) => {
      return !shouldDrop({
        url: log.http?.url,
        message: log.message,
        statusCode: log.http?.status_code,
      });
    },
  });
};

export const dataDogIdentify = (user: firebase.User | null): void => {
  if (!user || !isActivateDataDog()) return;
  const identity = {
    id: user.uid,
    email: user.email ?? undefined,
  };
  datadogLogs.setUser(identity);
};

/**
 * Attach business context (role, organiser, current show) to every Logs
 * event. Datadog dashboards can then filter by `@usr.role:organiser` or
 * `@show.id:abc` — invaluable when triaging an incident scoped to one
 * organiser or role. Impersonated sessions (admin→exhibitor/organiser/
 * supplier, organiser→exhibitor) can be filtered via
 * `@usr.impersonated_by_email:*`.
 */
const CONTEXT_PROP_OVERRIDES: Partial<
  Record<keyof ObservabilityContext, string>
> = {
  role: "usr.role",
  impersonatedByEmail: "usr.impersonated_by_email",
  impersonatedByRole: "usr.impersonated_by_role",
};

export const setDatadogContext = (ctx: ObservabilityContext): void => {
  if (!isActivateDataDog()) return;
  for (const [key, value] of Object.entries(ctx)) {
    const prop =
      CONTEXT_PROP_OVERRIDES[key as keyof ObservabilityContext] ?? key;
    if (value === null) {
      datadogLogs.removeGlobalContextProperty(prop);
    } else if (value !== undefined) {
      datadogLogs.setGlobalContextProperty(prop, value);
    }
  }
};

export const dataDogLogout = (): void => {
  if (!isActivateDataDog()) return;
  datadogLogs.clearUser();
  // Clear business context so the next user (or anonymous session) doesn't
  // inherit the previous user's role/organiser tags.
  datadogLogs.clearGlobalContext();
};

/**
 * Send a handled error to Datadog Logs. Use for caught exceptions where the
 * user sees a graceful fallback but we still want the failure recorded.
 * Axios errors are auto-enriched with method / URL / status / request-id /
 * backend code / truncated response body — callers normally just pass
 * `{ source: "..." }`.
 *
 * Caller-supplied context wins over auto-extracted fields, so explicit
 * overrides at a call site (e.g. `{ method: "POST" }`) take precedence.
 */
export const dataDogError = (
  error: unknown,
  context?: Record<string, unknown>
): void => {
  if (!isActivateDataDog()) return;
  const err = error instanceof Error ? error : new Error(String(error));
  const enriched: Record<string, unknown> = isAxiosLike(error)
    ? { ...extractAxiosFields(error), ...context }
    : { ...context };
  datadogLogs.logger.error(err.message, enriched, err);
};
